// Radar of the subdimensions (review V2 point 8, the TCU-style comparison): one axis per subdimension in definition
// order, starting at the top and going clockwise, rings at 25/50/75/100. Shared by the panel (React) and the PDF
// (SVG string). A missing score is never drawn as 0: a series is a closed polygon only when every axis has a value;
// otherwise only its present points are drawn.
import { GAUGE_COLORS, GAUGE_TRACK } from "./gauge.ts";
import type { CompareTree } from "./compare.ts";

export const RADAR_RINGS = [25, 50, 75, 100] as const;
export const RADAR_PENDING_NOTE = "Subdimensões pendentes não aparecem no gráfico.";

export interface RadarAxis { code: string; title: string; diagnosis: number | null; prognosis: number | null }
export interface RadarPoint { x: number; y: number }
export interface RadarSeries { points: (RadarPoint | null)[]; closed: boolean; present: number }
export interface RadarModel {
  width: number;
  height: number;
  cx: number;
  cy: number;
  r: number;
  rings: { value: number; points: RadarPoint[] }[];
  spokes: { end: RadarPoint; label: RadarPoint; anchor: "start" | "middle" | "end"; code: string; title: string }[];
  diagnosis: RadarSeries;
  prognosis: RadarSeries;
}

export const radarAxes = (t: CompareTree): RadarAxis[] =>
  t.subdimensions.map((s) => ({ code: s.code, title: s.title, diagnosis: s.diagnosis?.percent ?? null, prognosis: s.prognosis?.percent ?? null }));

/** "4110. Realizar planejamento…" → "Realizar planejamento…" (the starter repeats the code in the title). */
export function withoutCode(n: { code: string; title: string }) {
  if (!n.title.startsWith(n.code)) return n.title;
  const rest = n.title.slice(n.code.length);
  // Only when a separator follows the code ("A" must not turn "Avaliar" into "valiar").
  return /^[\s.\-–—:]/.test(rest) ? rest.replace(/^[\s.\-–—:]+/, "") || n.title : n.title;
}

export const radarSupported = (axes: readonly unknown[]) => axes.length >= 3;

export function radarModel(axes: RadarAxis[], opts: { width?: number; height?: number } = {}): RadarModel {
  const width = opts.width ?? 340;
  const height = opts.height ?? 300;
  const cx = width / 2;
  const cy = height / 2 + 4;
  const r = Math.min(width / 2 - 46, height / 2 - 26);
  const n = axes.length;
  const at = (i: number, value: number): RadarPoint => {
    const t = -Math.PI / 2 + (2 * Math.PI * i) / n;
    const k = (Math.min(100, Math.max(0, value)) / 100) * r;
    return { x: cx + k * Math.cos(t), y: cy + k * Math.sin(t) };
  };
  const series = (pick: (a: RadarAxis) => number | null): RadarSeries => {
    const points = axes.map((a, i) => (pick(a) === null ? null : at(i, pick(a)!)));
    const present = points.filter(Boolean).length;
    return { points, closed: present === n && n > 0, present };
  };
  return {
    width, height, cx, cy, r,
    rings: RADAR_RINGS.map((value) => ({ value, points: axes.map((_, i) => at(i, value)) })),
    spokes: axes.map((a, i) => {
      const end = at(i, 100);
      const t = -Math.PI / 2 + (2 * Math.PI * i) / n;
      const label = { x: cx + (r + 12) * Math.cos(t), y: cy + (r + 12) * Math.sin(t) + 4 };
      const c = Math.cos(t);
      return { end, label, anchor: Math.abs(c) < 0.2 ? "middle" : c > 0 ? "start" : "end", code: a.code, title: a.title };
    }),
    diagnosis: series((a) => a.diagnosis),
    prognosis: series((a) => a.prognosis),
  };
}

const pts = (p: RadarPoint[]) => p.map((q) => `${q.x.toFixed(2)},${q.y.toFixed(2)}`).join(" ");
const attr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

export const RADAR_STYLE = {
  diagnosis: { color: GAUGE_COLORS.diagnosis, fill: 0.16, dash: "" },
  prognosis: { color: GAUGE_COLORS.prognosis, fill: 0.08, dash: "6 4" },
} as const;

/** SVG markup for the PDF. `show` limits which series are drawn (the diagnosis report shows only the diagnosis). */
export function radarSvg(axes: RadarAxis[], show: { diagnosis: boolean; prognosis: boolean }, opts: { width?: number; height?: number; label?: string } = {}) {
  const m = radarModel(axes, opts);
  const seriesSvg = (key: "diagnosis" | "prognosis") => {
    if (!show[key]) return "";
    const s = m[key];
    const st = RADAR_STYLE[key];
    const dash = st.dash ? ` stroke-dasharray="${st.dash}"` : "";
    const shape = s.closed
      ? `<polygon points="${pts(s.points as RadarPoint[])}" fill="${st.color}" fill-opacity="${st.fill}" stroke="${st.color}" stroke-width="2" stroke-linejoin="round"${dash}/>`
      : "";
    const dots = s.points.map((p) => (p ? `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="3.5" fill="${st.color}" stroke="#fff" stroke-width="1.5"/>` : "")).join("");
    return shape + dots;
  };
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${m.width} ${m.height}" width="${m.width}" height="${m.height}" role="img"${opts.label ? ` aria-label="${attr(opts.label)}"` : ""}>
    ${m.rings.map((g) => `<polygon points="${pts(g.points)}" fill="none" stroke="${GAUGE_TRACK}" stroke-width="${g.value === 100 ? 1.2 : 0.8}"/>`).join("")}
    ${m.spokes.map((s) => `<line x1="${m.cx}" y1="${m.cy}" x2="${s.end.x.toFixed(2)}" y2="${s.end.y.toFixed(2)}" stroke="${GAUGE_TRACK}" stroke-width="0.8"/>`).join("")}
    ${m.rings.map((g) => `<text x="${(m.cx + 3).toFixed(2)}" y="${(g.points[0]!.y + 3).toFixed(2)}" font-size="7" fill="#66788F" font-family="JetBrains Mono, monospace">${g.value}</text>`).join("")}
    ${seriesSvg("prognosis")}${seriesSvg("diagnosis")}
    ${m.spokes.map((s) => `<text x="${s.label.x.toFixed(2)}" y="${s.label.y.toFixed(2)}" text-anchor="${s.anchor}" font-size="9" font-weight="700" fill="#13253D" font-family="Inter, sans-serif">${attr(s.code)}</text>`).join("")}
  </svg>`;
}
